import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { opsApi } from "@/features/operations/api";

export default function AdminAttendance() {
    const qc = useQueryClient();
    const [form, setForm] = useState({
        staff_id: "",
        date: new Date().toISOString().slice(0, 10),
        hours: 8,
        notes: "",
    });

    const team = useQuery({
        queryKey: ["admin", "team-staff"],
        queryFn: () => opsApi.listTeam({ role: "staff" }),
    });

    const { data: rows } = useQuery({
        queryKey: ["admin", "attendance"],
        queryFn: () => opsApi.listAttendance(),
    });

    const { data: payroll } = useQuery({
        queryKey: ["admin", "payroll"],
        queryFn: () => opsApi.payrollSummary(),
    });

    const createMut = useMutation({
        mutationFn: (p: typeof form) => opsApi.createAttendance(p),
        onSuccess: () => {
            toast.success("Attendance recorded");
            qc.invalidateQueries({ queryKey: ["admin", "attendance"] });
            qc.invalidateQueries({ queryKey: ["admin", "payroll"] });
        },
        onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Failed"),
    });

    return (
        <div data-testid="admin-attendance-page" className="space-y-6">
            <header>
                <h1 className="text-2xl font-extrabold">Attendance & payroll</h1>
                <p className="text-sm text-muted-foreground">
                    Record staff shifts. Salary = hours × per-hour rate configured on the team profile.
                </p>
            </header>

            <div className="grid gap-6 md:grid-cols-3">
                <div className="rounded-2xl border bg-white p-5 md:col-span-1">
                    <div className="flex items-center gap-2 pb-2 text-sm font-bold">
                        <ClipboardList className="h-4 w-4" /> Log shift
                    </div>
                    <form
                        className="space-y-3"
                        onSubmit={(e) => {
                            e.preventDefault();
                            createMut.mutate(form);
                        }}
                    >
                        <div>
                            <Label htmlFor="staff">Staff</Label>
                            <select
                                id="staff"
                                data-testid="attendance-staff"
                                className="mt-1 w-full rounded-md border border-border bg-white p-2 text-sm"
                                value={form.staff_id}
                                onChange={(e) => setForm({ ...form, staff_id: e.target.value })}
                                required
                            >
                                <option value="">Select staff…</option>
                                {team.data?.items.map((m) => (
                                    <option key={m.id} value={m.id}>{m.full_name} (₹{m.salary_rate_per_hour}/hr)</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label htmlFor="date">Date</Label>
                            <Input id="date" data-testid="attendance-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
                        </div>
                        <div>
                            <Label htmlFor="hours">Hours</Label>
                            <Input id="hours" data-testid="attendance-hours" type="number" min="0" step="0.5" max="24" value={form.hours} onChange={(e) => setForm({ ...form, hours: Number(e.target.value) })} required />
                        </div>
                        <div>
                            <Label htmlFor="notes">Notes</Label>
                            <Input id="notes" data-testid="attendance-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                        </div>
                        <Button type="submit" className="bg-brand text-white" data-testid="attendance-submit" disabled={createMut.isPending}>
                            Record shift
                        </Button>
                    </form>
                </div>

                <div className="md:col-span-2 space-y-6">
                    <section>
                        <h2 className="mb-3 text-sm font-bold uppercase tracking-widest text-muted-foreground">Payroll (all-time)</h2>
                        <div className="overflow-x-auto rounded-2xl bg-white">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-black/5 text-xs uppercase tracking-widest text-muted-foreground">
                                    <tr>
                                        <th className="px-4 py-3">Staff</th>
                                        <th className="px-4 py-3">Role</th>
                                        <th className="px-4 py-3">Shifts</th>
                                        <th className="px-4 py-3">Hours</th>
                                        <th className="px-4 py-3">Payable</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payroll?.items.length ? (
                                        payroll.items.map((p) => (
                                            <tr key={p.staff_id} className="border-t border-border/40" data-testid={`payroll-row-${p.staff_id}`}>
                                                <td className="px-4 py-3 font-semibold">{p.full_name}</td>
                                                <td className="px-4 py-3">{p.role}</td>
                                                <td className="px-4 py-3">{p.shifts}</td>
                                                <td className="px-4 py-3">{p.total_hours}</td>
                                                <td className="px-4 py-3 font-bold">₹{p.total_salary.toLocaleString("en-IN")}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td className="px-4 py-6 text-center text-muted-foreground" colSpan={5}>No shifts recorded yet</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section>
                        <h2 className="mb-3 text-sm font-bold uppercase tracking-widest text-muted-foreground">Recent shifts</h2>
                        <div className="overflow-x-auto rounded-2xl bg-white">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-black/5 text-xs uppercase tracking-widest text-muted-foreground">
                                    <tr>
                                        <th className="px-4 py-3">Date</th>
                                        <th className="px-4 py-3">Staff</th>
                                        <th className="px-4 py-3">Hours</th>
                                        <th className="px-4 py-3">Salary</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows?.items.slice(0, 30).map((r) => {
                                        const s = team.data?.items.find((x) => x.id === r.staff_id);
                                        return (
                                            <tr key={r.id} className="border-t border-border/40">
                                                <td className="px-4 py-3">{r.date}</td>
                                                <td className="px-4 py-3">{s?.full_name ?? "—"}</td>
                                                <td className="px-4 py-3">{r.hours}</td>
                                                <td className="px-4 py-3 font-bold">₹{r.salary.toLocaleString("en-IN")}</td>
                                            </tr>
                                        );
                                    })}
                                    {!rows?.items.length && (
                                        <tr><td className="px-4 py-6 text-center text-muted-foreground" colSpan={4}>No shifts yet</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
